import { useCallback, useEffect, useState } from 'react';
import { sections } from '../sections/sections';

/**
 * Manages which section is active and handles scroll-based navigation.
 * Uses IntersectionObserver for efficient scroll detection.
 */
export function useSectionNavigation() {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeSection = sections[activeIndex];

  const goToSection = useCallback((index: number) => {
    const clamped = Math.max(0, Math.min(index, sections.length - 1));
    setActiveIndex(clamped);

    const el = document.getElementById(sections[clamped].id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const goNext = useCallback(() => {
    setActiveIndex((prev) => {
      const next = Math.min(prev + 1, sections.length - 1);
      const el = document.getElementById(sections[next].id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
      return next;
    });
  }, []);

  const goPrev = useCallback(() => {
    setActiveIndex((prev) => {
      const next = Math.max(prev - 1, 0);
      const el = document.getElementById(sections[next].id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
      return next;
    });
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown' || e.key === 'PageDown') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault();
        goPrev();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goNext, goPrev]);

  // IntersectionObserver for scroll-based section detection
  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    sections.forEach((section, index) => {
      const el = document.getElementById(section.id);
      if (!el) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setActiveIndex(index);
          }
        },
        { threshold: 0.5 },
      );

      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((obs) => obs.disconnect());
  }, []);

  return {
    activeIndex,
    activeSection,
    goToSection,
    goNext,
    goPrev,
    totalSections: sections.length,
  };
}
